import { FormEvent, ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  AtSymbolIcon,
  ChatBubbleLeftRightIcon,
  EllipsisVerticalIcon,
  FaceSmileIcon,
  FunnelIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PlusIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { DraggableModalPanel } from '../../components/ui/DraggableModalPanel';
import { FormActionBar } from '../../components/ui/FormActionBar';
import {
  PortalConversationFilterChip,
  PortalConversationItem,
  PortalConversationMessage,
  PortalConversationTone,
  portalConversationsMock,
} from './portalConversationsMock';

const toneClass: Record<PortalConversationTone, string> = {
  blue: 'bg-primary-50 text-primary-700',
  green: 'bg-emerald-50 text-emerald-700',
  violet: 'bg-violet-50 text-violet-700',
  orange: 'bg-orange-50 text-orange-700',
  sky: 'bg-sky-50 text-sky-700',
  amber: 'bg-amber-50 text-amber-700',
};

const chipOptions: { id: PortalConversationFilterChip; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'unread', label: 'Não lidas' },
  { id: 'mine', label: 'Minhas' },
  { id: 'mentioned', label: 'Mencionados' },
];

type NewConversationForm = {
  title: string;
  subject: string;
  projectId: string;
  firstMessage: string;
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function emptyForm(projectId: string): NewConversationForm {
  return {
    title: '',
    subject: '',
    projectId,
    firstMessage: '',
  };
}

export function PortalConversationsPage() {
  const [conversations, setConversations] = useState(portalConversationsMock.conversations);
  const [selectedId, setSelectedId] = useState(portalConversationsMock.conversations[0]?.id ?? null);
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState<PortalConversationFilterChip>('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [draft, setDraft] = useState('');
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [form, setForm] = useState<NewConversationForm>(
    emptyForm(portalConversationsMock.projects[0]?.id ?? 'crm'),
  );

  const unreadTotal = useMemo(
    () => conversations.reduce((sum, item) => sum + item.unreadCount, 0),
    [conversations],
  );

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('pt-BR');
    return conversations.filter((item) => {
      if (projectFilter !== 'all' && item.projectId !== projectFilter) return false;
      if (chip === 'unread' && item.unreadCount === 0) return false;
      if (chip === 'mine' && !item.isMine) return false;
      if (chip === 'mentioned' && !item.mentionedMe) return false;
      if (!normalized) return true;
      const haystack =
        `${item.title} ${item.subject} ${item.preview} ${item.projectName}`.toLocaleLowerCase('pt-BR');
      return haystack.includes(normalized);
    });
  }, [chip, conversations, projectFilter, query]);

  const selected = conversations.find((item) => item.id === selectedId) ?? filtered[0] ?? null;

  function selectConversation(id: string) {
    setSelectedId(id);
    setConversations((current) =>
      current.map((item) => (item.id === id ? { ...item, unreadCount: 0 } : item)),
    );
  }

  function sendMessage(event: FormEvent) {
    event.preventDefault();
    if (!selected || !draft.trim()) return;
    const message: PortalConversationMessage = {
      id: `msg-${Date.now()}`,
      authorName: 'Ana Souza',
      body: draft.trim(),
      whenLabel: 'agora',
      isMine: true,
    };
    setConversations((current) =>
      current.map((item) =>
        item.id === selected.id
          ? {
              ...item,
              preview: `Você: ${message.body}`,
              whenLabel: 'agora',
              messages: [...item.messages, message],
            }
          : item,
      ),
    );
    setDraft('');
  }

  function openNewModal() {
    setForm(
      emptyForm(
        projectFilter !== 'all' ? projectFilter : portalConversationsMock.projects[0]?.id ?? 'crm',
      ),
    );
    setNewModalOpen(true);
  }

  function updateForm<K extends keyof NewConversationForm>(key: K, value: NewConversationForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function submitNewConversation(event: FormEvent) {
    event.preventDefault();
    if (!form.title.trim() || !form.subject.trim()) return;
    const project =
      portalConversationsMock.projects.find((entry) => entry.id === form.projectId) ??
      portalConversationsMock.projects[0];
    const firstBody = form.firstMessage.trim() || 'Conversa iniciada.';
    const next: PortalConversationItem = {
      id: `conv-${Date.now()}`,
      title: form.title.trim(),
      subject: form.subject.trim(),
      preview: `Você: ${firstBody}`,
      whenLabel: 'agora',
      unreadCount: 0,
      projectId: project.id,
      projectName: project.name,
      tone: 'blue',
      statusLabel: 'Ativa',
      createdAtLabel: new Date().toLocaleDateString('pt-BR'),
      notificationsEnabled: true,
      isMine: true,
      mentionedMe: false,
      participants: [
        { id: 'me', name: 'Ana Souza', role: 'Patrocinadora' },
        { id: 'pm', name: 'Rafael Oliveira', role: 'Gerente do projeto' },
      ],
      messages: [
        {
          id: `msg-${Date.now()}`,
          authorName: 'Ana Souza',
          body: firstBody,
          whenLabel: 'agora',
          isMine: true,
        },
      ],
      files: [],
      links: [],
    };
    setConversations((current) => [next, ...current]);
    setSelectedId(next.id);
    setProjectFilter(project.id);
    setChip('all');
    setNewModalOpen(false);
  }

  return (
    <div className="flex h-[calc(100vh-7.5rem)] min-h-[36rem] flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Conversas</h1>
          <p className="mt-1 text-sm text-secondary-600">Organizadas por projeto e assunto</p>
        </div>
        <button type="button" onClick={openNewModal} className="erp-btn-primary inline-flex items-center gap-1.5">
          <PlusIcon className="h-4 w-4" aria-hidden />
          Nova conversa
        </button>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[20rem_minmax(0,1fr)_18rem]">
        <section className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
          <div className="space-y-3 border-b border-secondary-100 p-4">
            <label className="relative block">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar conversas..."
                className="w-full rounded-xl border border-secondary-200 bg-secondary-50 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-secondary-500">
                <FunnelIcon className="h-3.5 w-3.5" aria-hidden />
                Projeto
              </span>
              <select
                value={projectFilter}
                onChange={(event) => setProjectFilter(event.target.value)}
                className="w-full rounded-xl border border-secondary-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="all">Todos os projetos</option>
                {portalConversationsMock.projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex flex-wrap gap-1.5">
              {chipOptions.map((option) => {
                const active = chip === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setChip(option.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
                      active
                        ? 'bg-primary-500 text-white'
                        : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                    }`}
                  >
                    {option.label}
                    {option.id === 'unread' && unreadTotal > 0 ? (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                          active ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                        }`}
                      >
                        {unreadTotal}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <ul className="min-h-0 flex-1 overflow-y-auto">
            {filtered.map((item) => {
              const active = selected?.id === item.id;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => selectConversation(item.id)}
                    className={`flex w-full items-start gap-3 border-b border-secondary-100 px-4 py-3 text-left transition-colors ${
                      active ? 'bg-primary-50/80' : 'hover:bg-secondary-50'
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${toneClass[item.tone]}`}
                    >
                      <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-start justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-secondary-900">{item.title}</span>
                        <span className="shrink-0 text-xs text-secondary-400">{item.whenLabel}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-secondary-500">{item.projectName}</span>
                      <span className="mt-1 flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-secondary-600">{item.preview}</span>
                        {item.unreadCount > 0 ? (
                          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                            {item.unreadCount}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 ? (
              <li className="px-4 py-10 text-center text-sm text-secondary-500">
                Nenhuma conversa com esse filtro.
              </li>
            ) : null}
          </ul>
        </section>

        <section className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
          {selected ? (
            <>
              <header className="flex items-start justify-between gap-3 border-b border-secondary-100 px-4 py-3">
                <div className="flex min-w-0 items-start gap-3">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${toneClass[selected.tone]}`}
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold text-secondary-900">{selected.title}</h2>
                    <p className="mt-0.5 line-clamp-2 text-sm text-secondary-500">{selected.subject}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <div className="hidden items-center -space-x-2 sm:flex">
                    {selected.participants.slice(0, 3).map((participant) => (
                      <span
                        key={participant.id}
                        className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-primary-50 text-[10px] font-semibold text-primary-700"
                        title={participant.name}
                      >
                        {initials(participant.name)}
                      </span>
                    ))}
                    {selected.participants.length > 3 ? (
                      <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-secondary-100 text-[10px] font-semibold text-secondary-600">
                        +{selected.participants.length - 3}
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
                    aria-label="Chamada de vídeo"
                  >
                    <VideoCameraIcon className="h-5 w-5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
                    aria-label="Buscar na conversa"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
                    aria-label="Mais opções"
                  >
                    <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
                  </button>
                </div>
              </header>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
                {selected.messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </div>

              <form onSubmit={sendMessage} className="border-t border-secondary-100 p-4">
                <div className="flex items-end gap-2 rounded-2xl border border-secondary-200 bg-secondary-50 px-3 py-2">
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary-400 hover:bg-white hover:text-secondary-700"
                    aria-label="Anexar arquivo"
                  >
                    <PaperClipIcon className="h-5 w-5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary-400 hover:bg-white hover:text-secondary-700"
                    aria-label="Mencionar"
                  >
                    <AtSymbolIcon className="h-5 w-5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary-400 hover:bg-white hover:text-secondary-700"
                    aria-label="Emoji"
                  >
                    <FaceSmileIcon className="h-5 w-5" aria-hidden />
                  </button>
                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    rows={1}
                    placeholder="Escrever uma mensagem..."
                    className="max-h-28 min-h-[2.5rem] flex-1 resize-none bg-transparent py-2 text-sm text-secondary-900 placeholder:text-secondary-400 focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={!draft.trim()}
                    className="rounded-xl bg-primary-500 p-2.5 text-white hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Enviar mensagem"
                  >
                    <PaperAirplaneIcon className="h-5 w-5" aria-hidden />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-secondary-500">
              Selecione uma conversa para começar.
            </div>
          )}
        </section>

        <aside className="hidden min-h-0 flex-col gap-4 overflow-y-auto xl:flex">
          {selected ? (
            <>
              <section className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-secondary-900">Detalhes da conversa</h3>
                <dl className="mt-3 space-y-3 text-sm">
                  <DetailRow label="Projeto">
                    <Link
                      to={`/portal/projects/${selected.projectId}`}
                      className="font-semibold text-primary-700 hover:underline"
                    >
                      {selected.projectName}
                    </Link>
                  </DetailRow>
                  <DetailRow label="Assunto">
                    <span className="text-secondary-800">{selected.subject}</span>
                  </DetailRow>
                  <DetailRow label="Criada em">
                    <span className="text-secondary-800">{selected.createdAtLabel}</span>
                  </DetailRow>
                  <DetailRow label="Participantes">
                    <ul className="space-y-2">
                      {selected.participants.map((participant) => (
                        <li key={participant.id} className="flex items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                            {initials(participant.name)}
                          </span>
                          <span>
                            <span className="block font-medium text-secondary-900">{participant.name}</span>
                            <span className="block text-xs text-secondary-500">{participant.role}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </DetailRow>
                  <DetailRow label="Status">
                    <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                      {selected.statusLabel}
                    </span>
                  </DetailRow>
                  <DetailRow label="Notificações">
                    <span className="text-secondary-800">
                      {selected.notificationsEnabled ? 'Ativadas' : 'Desativadas'}
                    </span>
                  </DetailRow>
                </dl>
              </section>

              <section className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-secondary-900">Arquivos compartilhados</h3>
                <ul className="mt-3 space-y-2">
                  {selected.files.length === 0 ? (
                    <li className="text-sm text-secondary-500">Nenhum arquivo compartilhado.</li>
                  ) : (
                    selected.files.map((file) => (
                      <li
                        key={file.id}
                        className="flex items-center gap-2 rounded-xl border border-secondary-100 px-2.5 py-2"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-50 text-secondary-500">
                          <PaperClipIcon className="h-4 w-4" aria-hidden />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-secondary-900">{file.name}</p>
                          <p className="text-xs text-secondary-500">
                            {file.sizeLabel} · {file.dateLabel}
                          </p>
                        </div>
                        <a
                          href={file.downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-lg p-1.5 text-secondary-400 hover:bg-secondary-50 hover:text-primary-700"
                          aria-label={`Baixar ${file.name}`}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                        </a>
                      </li>
                    ))
                  )}
                </ul>
              </section>

              <section className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-secondary-900">Links relacionados</h3>
                  <button type="button" className="text-xs font-semibold text-primary-700 hover:underline">
                    + Vincular
                  </button>
                </div>
                <ul className="mt-3 space-y-2">
                  {selected.links.length === 0 ? (
                    <li className="text-sm text-secondary-500">Nenhum vínculo.</li>
                  ) : (
                    selected.links.map((link) => (
                      <li key={link.id}>
                        <Link
                          to={link.href}
                          className="flex items-center gap-2 rounded-xl border border-secondary-100 px-2.5 py-2 hover:bg-secondary-50"
                        >
                          <LinkIcon className="h-4 w-4 shrink-0 text-secondary-400" aria-hidden />
                          <span className="min-w-0 flex-1">
                            <span className="block text-[11px] font-semibold uppercase tracking-wide text-secondary-500">
                              {link.kindLabel}
                            </span>
                            <span className="block truncate text-sm font-medium text-secondary-900">
                              {link.title}
                            </span>
                          </span>
                          <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 text-secondary-400" aria-hidden />
                        </Link>
                      </li>
                    ))
                  )}
                </ul>
              </section>
            </>
          ) : null}
        </aside>
      </div>

      <DraggableModalPanel
        open={newModalOpen}
        title="Nova conversa"
        subtitle="Escolha o projeto e o assunto para iniciar a discussão."
        onClose={() => setNewModalOpen(false)}
        footer={
          <FormActionBar showCancel onCancel={() => setNewModalOpen(false)}>
            <button
              type="submit"
              form="portal-new-conversation-form"
              className="erp-btn-primary"
              disabled={!form.title.trim() || !form.subject.trim()}
            >
              Criar conversa
            </button>
          </FormActionBar>
        }
      >
        <form id="portal-new-conversation-form" onSubmit={submitNewConversation} className="space-y-4">
          <label className="block">
            <span className="erp-label">Projeto</span>
            <select
              value={form.projectId}
              onChange={(event) => updateForm('projectId', event.target.value)}
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              {portalConversationsMock.projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="erp-label">Título</span>
            <input
              value={form.title}
              onChange={(event) => updateForm('title', event.target.value)}
              required
              placeholder="Ex.: Integração com ERP"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>
          <label className="block">
            <span className="erp-label">Assunto</span>
            <input
              value={form.subject}
              onChange={(event) => updateForm('subject', event.target.value)}
              required
              placeholder="Resumo do tema da conversa"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>
          <label className="block">
            <span className="erp-label">Primeira mensagem</span>
            <textarea
              value={form.firstMessage}
              onChange={(event) => updateForm('firstMessage', event.target.value)}
              rows={3}
              placeholder="Opcional — escreva a mensagem inicial"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>
        </form>
      </DraggableModalPanel>
    </div>
  );
}

function MessageBubble({ message }: { message: PortalConversationMessage }) {
  return (
    <div className={`flex gap-3 ${message.isMine ? 'flex-row-reverse' : ''}`}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[11px] font-semibold text-primary-700">
        {initials(message.authorName)}
      </span>
      <div className={`max-w-[min(32rem,85%)] ${message.isMine ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className={`flex items-center gap-2 ${message.isMine ? 'flex-row-reverse' : ''}`}>
          <p className="text-sm font-semibold text-secondary-900">{message.authorName}</p>
          <p className="text-xs text-secondary-400">{message.whenLabel}</p>
        </div>
        <div
          className={`mt-1 rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
            message.isMine
              ? 'rounded-tr-md bg-primary-500 text-white'
              : 'rounded-tl-md bg-secondary-100 text-secondary-800'
          }`}
        >
          <MessageBody body={message.body} mentions={message.mentions} isMine={message.isMine} />
        </div>
        {message.attachment ? (
          <a
            href={message.attachment.downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-2 rounded-xl border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-800 shadow-sm hover:bg-secondary-50"
          >
            <PaperClipIcon className="h-4 w-4 text-secondary-500" aria-hidden />
            <span>
              <span className="block font-medium">{message.attachment.name}</span>
              <span className="block text-xs text-secondary-500">{message.attachment.sizeLabel}</span>
            </span>
            <ArrowDownTrayIcon className="h-4 w-4 text-primary-600" aria-hidden />
          </a>
        ) : null}
        {message.reaction ? (
          <span className="mt-1 inline-flex rounded-full border border-secondary-200 bg-white px-2 py-0.5 text-xs shadow-sm">
            {message.reaction}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function MessageBody({
  body,
  mentions,
  isMine,
}: {
  body: string;
  mentions?: string[];
  isMine?: boolean;
}) {
  if (!mentions || mentions.length === 0) return <>{body}</>;

  const pattern = new RegExp(`(@(?:${mentions.join('|')}))`, 'g');
  const parts = body.split(pattern);
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith('@') ? (
          <span
            key={`${part}-${index}`}
            className={`font-semibold ${isMine ? 'text-primary-100' : 'text-primary-700'}`}
          >
            {part}
          </span>
        ) : (
          <span key={`${part}-${index}`}>{part}</span>
        ),
      )}
    </>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-secondary-500">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}
